import { SyntheticEvent, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { DevTool } from "@hookform/devtools";
import { paperClasses, Portal, Stack, Typography as T } from "@mui/material";
import Accordion, {
  AccordionSummary,
  AccordionDetails,
} from "datasets/pages/tables/components/Accordion";
import ImportDetails from "datasets/pages/tables/components/ImportDetails";
import ReviewDetails from "datasets/pages/tables/components/ReviewDetails";
import TableMetadataDetails from "datasets/pages/tables/components/TableMetadataDetails";
import useMetadataContext from "datasets/pages/tables/contexts/metadata";
import useActiveIngests from "datasets/pages/tables/hooks/useActiveIngests";
import { Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { normalizeColumnNames } from "datasets/pages/tables/utils/columns";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const IngestForm = () => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const { mutate: mutateActiveIngests } = useActiveIngests();
  const { setErrors } = useMetadataContext();
  const form = useForm<Ingest>({
    mode: "onChange",
    defaultValues: {
      file_size: 0,
      platform_storage_key: "",
      metadata_platform_storage_key: "",
      metadata_config: {
        columns: [],
        database_schema: "",
        table_description: "",
        table: "",
        tags: [],
      },
    },
  });

  const [expanded, setExpanded] = useState<string | false>("panel1");
  const storageKey = form.watch("platform_storage_key");
  const destination = form.watch("metadata_config.database_schema");
  const tableName = form.watch("metadata_config.table");
  const columns = form.watch("metadata_config.columns");

  // Check if we have valid columns for ingestion
  const isFormValidForIngestion = form.formState.isValid && columns && columns.length > 0;

  const handleExpand = (panel: string) => (_e: SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const onSubmit = async (data: Ingest) => {
    setErrors([]);

    try {
      const body = {
        dataset_id: datasetId,
        platform_storage_key: data.platform_storage_key,
        metadata_config: {
          ...data.metadata_config,
          columns: normalizeColumnNames(data.metadata_config.columns),
        },
      };

      const resp = await fetch(`${VITE_CATALOG_API}/ingests`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        const errorResp = await resp.json();
        const errorBody = JSON.parse(errorResp).details;
        const details = Array.isArray(errorBody) ? errorBody : [errorBody];

        return setErrors(details);
      }

      trackEvent("dataset_table_ingest_submitted", {
        dataset_id: datasetId,
        table: data.metadata_config.table,
        database_schema: data.metadata_config.database_schema,
        columns: data.metadata_config.columns.length,
      });

      mutateActiveIngests();
      navigate(`/data-browser/datasets/${datasetId}/tables`);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      showError("There was a problem attempting to ingest the data.");
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap={1}>
            {/* 1. Data Import */}
            <Accordion expanded={expanded === "panel1"} onChange={handleExpand("panel1")}>
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  1. Data Import
                </T>
              </AccordionSummary>
              <AccordionDetails
                sx={{ maxWidth: 690, [`.${paperClasses.root}`]: { maxWidth: 658 } }}
              >
                <ImportDetails onNextPanel={() => setExpanded("panel2")} />
              </AccordionDetails>
            </Accordion>

            {/* 2. Table Configuration */}
            <Accordion
              expanded={expanded === "panel2"}
              disabled={
                !(storageKey && destination && tableName) ||
                form.getFieldState("metadata_config.table").invalid
              }
              onChange={handleExpand("panel2")}
            >
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  2. Table Configuration
                </T>
              </AccordionSummary>
              <AccordionDetails>
                <TableMetadataDetails onNextPanel={() => setExpanded("panel3")} />
              </AccordionDetails>
            </Accordion>

            {/* 3. Review */}
            <Accordion
              expanded={expanded === "panel3"}
              disabled={!isFormValidForIngestion}
              onChange={handleExpand("panel3")}
            >
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  3. Review
                </T>
              </AccordionSummary>
              <AccordionDetails>
                <ReviewDetails />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </form>
      </FormProvider>
      {import.meta.env.DEV && (
        <Portal>
          <DevTool control={form.control} placement="bottom-right" />
        </Portal>
      )}
    </>
  );
};

export default IngestForm;
